import {useCallback, useEffect, useState} from "react";
import {Users} from "lucide-react";
import {
  createAdminClientGroup,
  deleteAdminClientGroup,
  listAdminClientGroups,
  updateAdminClientGroup,
  type AdminClientGroup
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs} from "./ui";

const emptyForm = {nume: "", discount: 0};

export function ClientGroupsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("clienti");
  const [items, setItems] = useState<AdminClientGroup[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminClientGroups({page, pageSize: 20, search: searchQuery || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, searchQuery]);
  useEffect(() => { load(); }, [load]);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(g: AdminClientGroup) {
    setOpenId(g.id);
    setForm({nume: g.nume, discount: g.discount});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminClientGroup(form);
          adminToast.success("Group added.");
        } else if(typeof openId === "number") {
          await updateAdminClientGroup(openId, form);
          adminToast.success("Group saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete group?", async () => {
      await deleteAdminClientGroup(id);
      closeModal(false);
      load();
      adminToast.success("Group deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.clienti.title}
      description="Client discount groups"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.clienti.title, href: "/vadmin/clienti"},
        {label: "Groups"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add group" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="clienti" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(g) => g.id}
        getTitle={(g) => g.nume}
        getMeta={(g) => (
          <>
            {g.discount !== 0 ? <AdminBadge variant="secondary">-{g.discount}%</AdminBadge> : null}
          </>
        )}
        getSearchText={(g) => adminSearchText(g.id, g.nume, g.discount)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No groups"
        emptyDescription="Add a new group."
        searchPlaceholder="Search group, discount..."
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New group" : "Edit group"}
        icon={Users}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminField label="Name">
            <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} placeholder="Group name" />
          </AdminField>
          <AdminField label="Discount">
            <Input type="number" value={form.discount} onChange={(e) => setForm({...form, discount: Number(e.target.value)})} />
          </AdminField>
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
